import { company } from './company';

const LAST_UPDATED = '17 July 2026';

const contactBlock = {
  heading: 'Contact Us',
  paragraphs: [
    `If you have questions about this policy or wish to exercise your rights, please contact us:`,
  ],
  bullets: [
    `Email: ${company.email}`,
    `Phone: ${company.phones[0].number}`,
    `Address: ${company.address.line1}, ${company.address.line2}, ${company.address.line3}`,
  ],
};

export const policies = {
  'privacy-policy': {
    slug: 'privacy-policy',
    path: '/privacy-policy',
    title: 'Privacy Policy',
    lastUpdated: LAST_UPDATED,
    intro:
      `This Privacy Policy explains how ${company.legalName} ("we", "us", or "our") collects, uses, and protects personal information when you visit ojanna.co.za or contact us. We process personal information in accordance with South Africa's Protection of Personal Information Act (POPIA).`,
    sections: [
      {
        heading: 'Who We Are',
        paragraphs: [
          `${company.legalName} is a distributor of ceiling, drywall, insulation and acoustic products based in George, South Africa. We are the responsible party for personal information processed in connection with this website.`,
        ],
      },
      {
        heading: 'Information We Collect',
        paragraphs: [
          'We may collect the following personal information:',
        ],
        bullets: [
          'Identity and contact details you provide via our contact form (name, email address, phone number, and message content)',
          'Product interest or enquiry details you choose to share',
          'Technical and usage data collected through cookies and similar technologies (see our Cookie Policy)',
          'Communications you send us by email, phone, or WhatsApp',
        ],
      },
      {
        heading: 'How We Use Your Information',
        paragraphs: [
          'We use personal information to:',
        ],
        bullets: [
          'Respond to enquiries and provide quotes or product information',
          'Operate, maintain, and improve our website',
          'Measure site performance and understand how visitors use our site (where you consent to analytics cookies)',
          'Support advertising and remarketing activities (where you consent to advertising cookies)',
          'Comply with legal obligations and protect our legitimate business interests',
        ],
      },
      {
        heading: 'Legal Basis for Processing',
        paragraphs: [
          'Depending on the context, we process personal information because:',
        ],
        bullets: [
          'You have given consent (for example, cookie preferences or voluntary form submissions)',
          'Processing is necessary to take steps at your request before entering into a contract, or to perform a contract',
          'Processing is necessary for our legitimate interests, such as operating our website and responding to business enquiries, provided those interests are not overridden by your rights',
          'Processing is required to comply with a legal obligation',
        ],
      },
      {
        heading: 'Sharing of Information',
        paragraphs: [
          'We do not sell your personal information. We may share information with:',
        ],
        bullets: [
          'Service providers who help us operate the website (for example hosting, email delivery, and analytics platforms), under appropriate agreements',
          'Professional advisers or authorities where required by law',
          'Other parties with your consent or where needed to respond to your enquiry',
        ],
      },
      {
        heading: 'Cookies and Similar Technologies',
        paragraphs: [
          'We use cookies and similar technologies as described in our Cookie Policy. You can manage non-essential cookies through our cookie banner. Preferences are stored locally in your browser.',
        ],
      },
      {
        heading: 'International Transfers',
        paragraphs: [
          'Some service providers (such as analytics or hosting providers) may process data outside South Africa. Where this occurs, we take reasonable steps to ensure an adequate level of protection consistent with POPIA.',
        ],
      },
      {
        heading: 'Retention',
        paragraphs: [
          'We keep personal information only for as long as needed for the purposes described above, or as required by law. Contact form enquiries are retained for a reasonable period to manage follow-up and business records, then deleted or anonymised when no longer needed.',
        ],
      },
      {
        heading: 'Your Rights Under POPIA',
        paragraphs: [
          'Subject to applicable law, you may have the right to:',
        ],
        bullets: [
          'Request access to the personal information we hold about you',
          'Request correction or deletion of inaccurate or outdated information',
          'Object to or restrict certain processing',
          'Withdraw consent where processing is based on consent (this does not affect processing already carried out)',
          'Lodge a complaint with the Information Regulator (South Africa)',
        ],
      },
      {
        heading: 'Security',
        paragraphs: [
          'We take reasonable technical and organisational measures to protect personal information against loss, misuse, and unauthorised access. No method of transmission over the internet is completely secure, and we cannot guarantee absolute security.',
        ],
      },
      {
        heading: 'Third-Party Links',
        paragraphs: [
          'Our website may contain links to third-party sites (for example maps, partner brands, or messaging apps). We are not responsible for the privacy practices of those sites.',
        ],
      },
      {
        heading: 'Changes to This Policy',
        paragraphs: [
          'We may update this Privacy Policy from time to time. The "Last updated" date at the top of this page will reflect the latest revision. Continued use of the website after changes constitutes acceptance of the updated policy where permitted by law.',
        ],
      },
      contactBlock,
    ],
  },

  'cookie-policy': {
    slug: 'cookie-policy',
    path: '/cookie-policy',
    title: 'Cookie Policy',
    lastUpdated: LAST_UPDATED,
    intro:
      `This Cookie Policy explains how ${company.legalName} uses cookies and similar technologies on ojanna.co.za, and how you can manage your preferences.`,
    sections: [
      {
        heading: 'What Are Cookies?',
        paragraphs: [
          'Cookies are small text files stored on your device when you visit a website. They help the site function, remember preferences, and (where allowed) measure performance or support advertising.',
        ],
      },
      {
        heading: 'How We Use Cookies',
        paragraphs: [
          'We use cookies for:',
        ],
        bullets: [
          'Remembering your cookie consent preferences',
          'Website analytics and performance measurement (if you allow analytics cookies)',
          'Advertising and remarketing (if you allow advertising cookies)',
        ],
      },
      {
        heading: 'Types of Cookies We Use',
        paragraphs: [
          'Necessary / preference cookies: Used to store your consent choices so we do not ask you every visit. These are required for the consent mechanism to work.',
          'Analytics cookies: Help us understand how visitors use the site (for example pages viewed and traffic sources). We only enable analytics storage when you consent.',
          'Advertising cookies: Used for personalised advertising and remarketing. We only enable advertising-related storage when you consent.',
        ],
      },
      {
        heading: 'Consent and Google Consent Mode',
        paragraphs: [
          'When you first visit our site, a cookie banner allows you to Accept All, choose Necessary Only, Reject All, or Customise your preferences for analytics and advertising cookies.',
          'We use Google Consent Mode so that analytics and advertising tags respect your choices. Until you grant consent for a category, related storage remains denied.',
        ],
      },
      {
        heading: 'How Long Preferences Are Stored',
        paragraphs: [
          'Your cookie preferences are stored in your browser (local storage key: ojanna_consent_preferences) for up to 365 days. After that, or if you clear site data, we will ask again.',
        ],
      },
      {
        heading: 'Managing Your Preferences',
        paragraphs: [
          'You can change your mind at any time by clearing this site\'s cookies and local storage in your browser settings, then revisiting the site to use the cookie banner again. You can also control cookies through your browser settings; blocking some cookies may affect site functionality.',
        ],
      },
      {
        heading: 'More Information',
        paragraphs: [
          'For details on how we handle personal information more broadly, please read our Privacy Policy.',
        ],
      },
      contactBlock,
    ],
  },

  'terms-of-use': {
    slug: 'terms-of-use',
    path: '/terms-of-use',
    title: 'Website Terms of Use',
    lastUpdated: LAST_UPDATED,
    intro:
      `These Terms of Use govern your access to and use of the website ojanna.co.za operated by ${company.legalName}. By using this website, you agree to these terms.`,
    sections: [
      {
        heading: 'About This Website',
        paragraphs: [
          `This website provides information about ${company.name}, our products, brands we distribute, and how to contact us. Content is for general information and does not constitute a binding offer unless we expressly confirm otherwise in writing.`,
        ],
      },
      {
        heading: 'Acceptable Use',
        paragraphs: [
          'You agree not to:',
        ],
        bullets: [
          'Use the site in any unlawful way or for fraudulent purposes',
          'Attempt to gain unauthorised access to our systems or disrupt the site',
          'Scrape, copy, or reuse substantial parts of the site without our prior written permission',
          'Submit false, misleading, or abusive content through forms or other channels',
        ],
      },
      {
        heading: 'Product Information and Pricing',
        paragraphs: [
          'Product descriptions, brand information, and any indicative pricing on this site are provided for convenience and may change without notice. Availability, specifications, and prices should be confirmed with us before ordering. Nothing on this website creates a contract of sale until we accept an order on agreed terms.',
        ],
      },
      {
        heading: 'Intellectual Property',
        paragraphs: [
          'Website design, text, graphics, and our branding are owned by us or our licensors. Brand names and logos of manufacturers we distribute remain the property of their respective owners. You may not use our materials or third-party brand assets without permission.',
        ],
      },
      {
        heading: 'Third-Party Links and Services',
        paragraphs: [
          'Links to maps, messaging apps, partner sites, or other third parties are provided for convenience. We do not control and are not responsible for those services or their content.',
        ],
      },
      {
        heading: 'Disclaimer',
        paragraphs: [
          'This website is provided on an "as is" and "as available" basis. To the fullest extent permitted by South African law, we disclaim warranties regarding uninterrupted access, accuracy of all content, or fitness for a particular purpose. We take reasonable care with information but errors or omissions may occur.',
        ],
      },
      {
        heading: 'Limitation of Liability',
        paragraphs: [
          'To the fullest extent permitted by law, we are not liable for any indirect, incidental, or consequential loss arising from your use of, or inability to use, this website. Nothing in these terms excludes liability that cannot be excluded under applicable law.',
        ],
      },
      {
        heading: 'Privacy',
        paragraphs: [
          'Personal information is handled in accordance with our Privacy Policy and Cookie Policy.',
        ],
      },
      {
        heading: 'Changes',
        paragraphs: [
          'We may update these Terms of Use from time to time. The "Last updated" date will change when we do. Continued use of the site after updates means you accept the revised terms where permitted by law.',
        ],
      },
      {
        heading: 'Governing Law',
        paragraphs: [
          'These terms are governed by the laws of the Republic of South Africa. Courts of South Africa have jurisdiction over disputes arising from these terms or use of the website.',
        ],
      },
      contactBlock,
    ],
  },
};

export const policyList = Object.values(policies);

export function getPolicyBySlug(slug) {
  return policies[slug] || null;
}

export function getPolicyByPath(path) {
  return policyList.find((p) => p.path === path) || null;
}
